import { Fragment } from 'react';
import { EMPTY_VALUE } from '@/constants/app.constants';
import { cn } from '@/lib/utils';
import { formatNumber } from '@/utils/number';
import type { PcmpMoFigures, PcmpMoInfo, PcmpMoReport } from '../../types/pcmpMoReport.types';

const cellClass = 'border border-neutral-300 px-2 py-1';
const headClass = cn(cellClass, 'bg-neutral-100 text-center font-semibold text-neutral-900');
const numClass = 'text-right tabular-nums whitespace-nowrap';

const amount = (value: number) =>
  value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
const text = (value: string | null) => (value && value.trim() !== '' ? value : EMPTY_VALUE);

type Block = 'mkt' | 'acc';
const BLOCK_KEYS = ['processing', 'notVerified', 'disbursed', 'hold', 'total', 'return'] as const;
const BLOCK_LABELS = {
  processing: 'Processing',
  notVerified: 'Not Verified',
  disbursed: 'Disbursed',
  hold: 'Hold',
  total: 'Total',
  return: 'Return',
} as const;
const BLOCK_TITLES: Record<Block, string> = { mkt: 'Marketing Payment', acc: 'Accounts Payment' };

interface FigureField {
  label: string;
  /** The block this field belongs to, for the group header. */
  group: Block | null;
  isCount: boolean;
  /** Totals read bold. */
  isTotal: boolean;
  get: (figures: PcmpMoFigures) => number;
}

/** Every figure of an MO, in the order the sheet shows it. */
const FIELDS: FigureField[] = [
  { label: 'Proposals', group: null, isCount: true, isTotal: false, get: (f) => f.proposalQty },
  { label: 'Institutes', group: null, isCount: true, isTotal: false, get: (f) => f.instituteQty },
  ...(['mkt', 'acc'] as const).flatMap((block) =>
    BLOCK_KEYS.map(
      (key): FigureField => ({
        label: BLOCK_LABELS[key],
        group: block,
        isCount: false,
        isTotal: key === 'total',
        get: (f) => f[block][key],
      }),
    ),
  ),
  {
    label: 'Total Disbursed',
    group: null,
    isCount: false,
    isTotal: true,
    get: (f) => f.totalDisbursed,
  },
  { label: 'Total Return', group: null, isCount: false, isTotal: true, get: (f) => f.totalReturn },
  { label: 'Grand Total', group: null, isCount: false, isTotal: true, get: (f) => f.grandTotal },
];

const format = (field: FigureField, figures: PcmpMoFigures) =>
  field.isCount ? formatNumber(field.get(figures)) : amount(field.get(figures));

function MoInfoRows({ mo }: { mo: PcmpMoInfo }) {
  const rows: [string, string | null][] = [
    ['MO', mo.name],
    ['HR ID', mo.hrId],
    ['Mobile', mo.mobile],
    ['Location', mo.location],
    ['Department', mo.department],
    ['Division', mo.division],
    ['Reporting', mo.reporting],
  ];
  return (
    <dl className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs sm:grid-cols-4">
      {rows.map(([label, value]) => (
        <div key={label} className="flex gap-1.5">
          <dt className="text-neutral-500">{label}:</dt>
          <dd className="font-medium">{text(value)}</dd>
        </div>
      ))}
    </dl>
  );
}

/** One MO: the figures down, a column per project and a Total column. */
function SingleMoTable({ report }: { report: PcmpMoReport }) {
  const figuresOf = new Map(report.byProject.map((item) => [item.projectId, item.figures]));

  return (
    <div className="flex flex-col gap-3">
      {report.mo ? <MoInfoRows mo={report.mo} /> : null}
      <table className="w-full border-collapse text-xs">
        <thead>
          <tr>
            <th className={headClass}>Figure</th>
            {report.projects.map((project) => (
              <th key={project.id} className={headClass}>
                {project.name}
              </th>
            ))}
            <th className={headClass}>Total</th>
          </tr>
        </thead>
        <tbody>
          {FIELDS.map((field, index) => {
            const previous = FIELDS[index - 1];
            const startsGroup = field.group !== null && previous?.group !== field.group;
            return (
              <Fragment key={`${field.group ?? 'all'}-${field.label}`}>
                {startsGroup && field.group ? (
                  <tr>
                    <td
                      colSpan={report.projects.length + 2}
                      className={cn(cellClass, 'bg-neutral-100 font-semibold')}
                    >
                      {BLOCK_TITLES[field.group]}
                    </td>
                  </tr>
                ) : null}
                <tr className={cn(field.isTotal && 'bg-neutral-50 font-semibold')}>
                  <td className={cn(cellClass, field.group && 'pl-4')}>{field.label}</td>
                  {report.projects.map((project) => {
                    const figures = figuresOf.get(project.id);
                    return (
                      <td key={project.id} className={cn(cellClass, numClass)}>
                        {figures ? format(field, figures) : EMPTY_VALUE}
                      </td>
                    );
                  })}
                  <td className={cn(cellClass, numClass, 'font-semibold')}>
                    {format(field, report.total)}
                  </td>
                </tr>
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// The columns before, inside and after the two payment groups.
const LEADING_FIELDS = FIELDS.filter((field) => field.group === null).slice(0, 2);
const TRAILING_FIELDS = FIELDS.filter((field) => field.group === null).slice(2);
const BLOCK_FIELDS = FIELDS.filter((field) => field.group !== null);
const BLOCK_SPAN = BLOCK_KEYS.length;

/** Many MOs: a row per MO and a grand total row. */
function ManyMosTable({ report }: { report: PcmpMoReport }) {
  return (
    <table className="w-full border-collapse text-xs">
      <thead>
        <tr>
          {['MO', 'HR ID', 'Department', 'Division', 'Reporting'].map((heading) => (
            <th key={heading} rowSpan={2} className={headClass}>
              {heading}
            </th>
          ))}
          {LEADING_FIELDS.map((field) => (
            <th key={field.label} rowSpan={2} className={headClass}>
              {field.label}
            </th>
          ))}
          <th colSpan={BLOCK_SPAN} className={headClass}>
            {BLOCK_TITLES.mkt}
          </th>
          <th colSpan={BLOCK_SPAN} className={headClass}>
            {BLOCK_TITLES.acc}
          </th>
          {TRAILING_FIELDS.map((field) => (
            <th key={field.label} rowSpan={2} className={headClass}>
              {field.label}
            </th>
          ))}
        </tr>
        <tr>
          {BLOCK_FIELDS.map((field) => (
            <th key={`${field.group}-${field.label}`} className={headClass}>
              {field.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {report.lines.map((line) => (
          <tr key={line.mo.userId}>
            <td className={cn(cellClass, 'whitespace-nowrap')}>{line.mo.name}</td>
            <td className={cn(cellClass, 'whitespace-nowrap')}>{text(line.mo.hrId)}</td>
            <td className={cellClass}>{line.mo.department}</td>
            <td className={cellClass}>{line.mo.division}</td>
            <td className={cellClass}>{text(line.mo.reporting)}</td>
            {FIELDS.map((field) => (
              <td
                key={`${field.group}-${field.label}`}
                className={cn(cellClass, numClass, field.isTotal && 'font-semibold')}
              >
                {format(field, line.figures)}
              </td>
            ))}
          </tr>
        ))}
        <tr className="bg-neutral-200 font-bold">
          <td colSpan={5} className={cellClass}>
            Grand Total
          </td>
          {FIELDS.map((field) => (
            <td key={`${field.group}-${field.label}`} className={cn(cellClass, numClass)}>
              {format(field, report.total)}
            </td>
          ))}
        </tr>
      </tbody>
    </table>
  );
}

/** The MO Wise report: the layout follows the scope. */
export function PcmpMoReportTable({ report }: { report: PcmpMoReport }) {
  return report.scope === 'MO' ? (
    <SingleMoTable report={report} />
  ) : (
    <ManyMosTable report={report} />
  );
}
